import React, { useEffect } from "react";
import posthog from "posthog-js";
import Button from "../components/button";
import { ShowErrorMsg } from "../utils/usePopupMessage";

declare global {
  interface Window {
    mtcaptcha?: any;
    mtcaptchaConfig?: any;
  }
}

interface CaptchaDialogProps {
  isOpen: boolean;
  onClose: () => void;
  setStep: React.Dispatch<React.SetStateAction<number>>;
  setCaptchaToken: React.Dispatch<React.SetStateAction<string | null>>;
  setVerifiedToken: React.Dispatch<React.SetStateAction<string | null>>;
  captchaToken: string | null;
  setInvitationCode: React.Dispatch<React.SetStateAction<string>>;
  loading: boolean;
  setLoading: React.Dispatch<React.SetStateAction<boolean>>;
}

const CaptchaDialog = ({
  isOpen,
  onClose,
  setStep,
  captchaToken,
  setCaptchaToken,
  setVerifiedToken,
  loading,
  setLoading,
  setInvitationCode,
}: CaptchaDialogProps) => {
  useEffect(() => {
    if (!isOpen) return;

    window.mtcaptchaConfig = {
      sitekey: process.env.REACT_APP_MTCAPTCHA_SITEKEY,
      lang: "vi",
      autoFormValidate: true,
      widgetSize: "normal",
      "verified-callback": function (status: any) {
        const token =
          status?.verifiedToken || window.mtcaptcha?.getVerifiedToken?.();
        document.dispatchEvent(
          new CustomEvent("mtcaptcha-verified", { detail: token })
        );
      },
    };

    const handleVerified = (event: Event) => {
      const customEvent = event as CustomEvent<string>;
      setCaptchaToken(customEvent.detail);
    };

    document.addEventListener("mtcaptcha-verified", handleVerified);

    const script1 = document.createElement("script");
    script1.src = "https://service.mtcaptcha.com/mtcv1/client/mtcaptcha.min.js";
    script1.async = true;

    const script2 = document.createElement("script");
    script2.src =
      "https://service2.mtcaptcha.com/mtcv1/client/mtcaptcha2.min.js";
    script2.async = true;

    document.body.appendChild(script1);
    document.body.appendChild(script2);

    window.mtcaptcha?.renderUI?.();
  }, [isOpen, setCaptchaToken]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);

    if (!captchaToken) {
      ShowErrorMsg("Vui lòng hoàn tất CAPTCHA.");
      return;
    }

    posthog.capture("CheckVerifyCode_Button_Click", {
      button_location: "CaptchaDialog",
      is_formal_action: true,
      timestamp: new Date().toISOString(),
    });

    await new Promise((resolve) => setTimeout(resolve, 300)); // adjust as needed

    try {
      const requestParam = {
        VerifyCode: captchaToken,
        DistinctId: posthog.get_distinct_id(),
      };

      const response = await fetch(
        `${process.env.REACT_APP_API_URL}/Promotion/CheckVerifyCode`,
        {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify(requestParam),
        }
      );

      const result = await response.json();

      if (result?.code === 200) {
        setVerifiedToken(result.data);
        setStep(3);
        onClose();
      } else if (response.status === 400) {
        setCaptchaToken(null);
        setInvitationCode("");
        return;
      } else {
        ShowErrorMsg(result.message || "Xác minh thất bại.");
        setCaptchaToken(null);
        setInvitationCode("");
        onClose();
      }
    } catch (error) {
      console.error("Captcha error:", error);
    } finally {
      setLoading(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-40 flex items-center justify-center bg-black bg-opacity-50">
      <div className="bg-white text-center rounded-lg shadow-lg p-6 w-full max-w-sm md:max-w-md relative">
        <button
          onClick={onClose}
          className="absolute top-4 right-4 font-semibold text-gray-600 hover:text-red-500"
        >
          X
        </button>
        <h2 className="text-xl font-semibold mb-4">MÃ XÁC THỰC</h2>
        <form
          onSubmit={handleSubmit}
          className="flex flex-col justify-end items-center"
        >
          <div className="mtcaptcha mb-4" />
          <Button type="submit" text={"XÁC NHẬN"} disabled={loading} />
        </form>
      </div>
    </div>
  );
};

export default CaptchaDialog;
